<template>
  <div class="seo-page game-pass-deck">
    <section class="seo-hero">
      <span class="seo-badge">Steam Deck</span>
      <h1>Xbox PC and Game Pass Games Are Coming to Steam Deck</h1>
      <p>
        The Heroic Games Launcher team and the wider open-source community are building Xodus, a project that aims to run
        Xbox PC titles and your PC Game Pass library natively on Steam Deck - installed locally, with no cloud streaming.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Launch steamplayti.me</router-link>
        <a class="seo-secondary-link" href="#status">See where it stands →</a>
      </div>
      <ul class="seo-highlight-list">
        <li v-for="highlight in highlights" :key="highlight.copy">
          <span class="seo-highlight-icon">{{ highlight.icon }}</span>
          <div>{{ highlight.copy }}</div>
        </li>
      </ul>
    </section>

    <section class="seo-section">
      <h2>Why Game Pass Games Refused to Launch</h2>
      <p>
        Proton can already translate the graphics layer, but games installed through the Xbox app lean on Microsoft's own
        GDK stack: encrypted MSIXVC packages and Xbox authentication services. None of that exists on the Deck, so the
        games either crashed instantly or never opened at all.
      </p>
    </section>

    <section class="seo-section" id="status">
      <h2>Where Xodus Stands Today</h2>
      <div class="seo-grid">
        <article v-for="item in status" :key="item.title" class="seo-card">
          <h3>{{ item.title }}</h3>
          <p><strong>{{ item.state }}</strong></p>
          <p>{{ item.detail }}</p>
        </article>
      </div>
    </section>

    <section class="seo-section">
      <h2>Which Games Are in Scope</h2>
      <p>
        The focus is on newer PC releases built on Microsoft's current GDK architecture and the MSIXVC package format,
        including Xbox Play Anywhere titles. The older first-generation UWP Windows Store games are not a priority target.
      </p>
      <p>
        Click-and-play Game Pass on Steam Deck is still some way off, but the hardest barrier - download, licensing, and
        decryption - is already behind the project. Once the runtime lands, Deck owners will be able to install Game Pass
        games directly instead of streaming them.
      </p>
    </section>
    <SeoRelatedTools heading="Track Your Own Steam Playtime" />
  </div>
</template>

<script>
import { applyMeta, articleStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const HIGHLIGHTS = [
  { icon: '🎮', copy: 'Xodus targets native Xbox PC and Game Pass installs on Steam Deck - no cloud streaming.' },
  { icon: '🔓', copy: 'Sign-in, downloads, licensing, and MSIXVC decryption already work.' },
  { icon: '🧩', copy: 'Next up: rebuilding the Xbox Gaming Runtime (xgameruntime.dll) for Wine and Proton.' },
];

const STATUS = [
  {
    title: 'Authentication',
    state: 'Working',
    detail: 'Signing in with a Microsoft / Xbox account is in place.',
  },
  {
    title: 'Downloads & licensing',
    state: 'Working',
    detail: 'Game packages download, licenses are pulled, and encrypted MSIXVC files decrypt successfully.',
  },
  {
    title: 'Actually playing',
    state: 'Not yet',
    detail: 'PC Game Pass games are not playable on the Deck yet - the Xbox Gaming Runtime still has to be reimplemented.',
  },
];

const META = {
  title: 'Xbox Game Pass on Steam Deck: The Xodus Project | steamplayti.me',
  description: 'Heroic Games Launcher devs are building Xodus to run Xbox PC and Game Pass games natively on Steam Deck. Sign-in, downloads, and MSIXVC decryption already work.',
  keywords: 'Xbox Game Pass Steam Deck, Xodus project, Heroic Games Launcher, MSIXVC, xgameruntime, Proton',
  canonical: 'https://steamplayti.me/blog/game-pass-steam-deck-xodus/',
  ogTitle: 'Xbox Game Pass Games Are Coming to Steam Deck',
  ogDescription: 'The Xodus project already handles Xbox sign-in, downloads, and MSIXVC decryption. The Xbox Gaming Runtime is the last big hurdle.',
  ogUrl: 'https://steamplayti.me/blog/game-pass-steam-deck-xodus/',
  twitterTitle: 'Game Pass on Steam Deck: Meet Xodus',
  twitterDescription: 'Native Xbox PC and Game Pass installs on Steam Deck are getting close - here is what already works.',
  structuredData: articleStructuredData({
    headline: 'Xbox PC and Game Pass Games Are Coming to Steam Deck',
    description: 'Heroic Games Launcher devs are building Xodus to run Xbox PC and Game Pass games natively on Steam Deck. Sign-in, downloads, and MSIXVC decryption already work.',
    canonical: 'https://steamplayti.me/blog/game-pass-steam-deck-xodus/',
    datePublished: '2026-08-14',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Xbox Game Pass Games Are Coming to Steam Deck', path: 'https://steamplayti.me/blog/game-pass-steam-deck-xodus/' },
    ],
  }),
};

export default {
  name: 'GamePassSteamDeck',
  components: { SeoRelatedTools },
  data() {
    return {
      highlights: HIGHLIGHTS,
      status: STATUS,
    };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style scoped>
.game-pass-deck {
  background: radial-gradient(circle at top left, rgba(76, 214, 129, 0.14), transparent 65%);
}
</style>
